import { environment } from "environments/environment";
import * as Sentry from "@sentry/browser";
import { ErrorHandler, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable({ providedIn: "root" })
export class SentryErrorHandler implements ErrorHandler {
  public handleError(err: unknown, data?: unknown): void {
    if (environment.sentryEnabled) {
      const extra: Record<string, unknown> = {};
      if (data) {
        extra["additional_info"] = data;
      }
      this.captureException(this.toError(err, extra), { extra });
    }

    console.error(err);
  }

  protected captureException(
    error: Error,
    options: { extra: Record<string, unknown> },
  ): void {
    Sentry.captureException(error, options);
  }

  // Sentry only builds a useful issue from real Error instances. Passing a raw
  // HttpErrorResponse (or any plain object) makes it log "Object captured as
  // exception with keys: ...", so normalize everything into an Error first and
  // keep the original payload in `extra`.
  private toError(err: unknown, extra: Record<string, unknown>): Error {
    const original =
      err && typeof err === "object" && "ngOriginalError" in err
        ? (err as { ngOriginalError: unknown }).ngOriginalError
        : err;

    if (original instanceof HttpErrorResponse) {
      extra["httpError"] = {
        status: original.status,
        statusText: original.statusText,
        url: original.url,
        message: original.message,
      };
      return new Error(
        `HTTP ${original.status} ${original.statusText} for ${original.url ?? "unknown url"}`,
      );
    }

    if (original instanceof Error) {
      return original;
    }

    if (typeof original === "string") {
      return new Error(original);
    }

    extra["originalError"] = original;
    return new Error(this.describe(original));
  }

  private describe(value: unknown): string {
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      const message = record["message"] ?? record["statusText"];
      if (typeof message === "string" && message.length > 0) {
        return message;
      }
      return `Non-Error thrown with keys: ${Object.keys(record).join(", ")}`;
    }
    return `Non-Error thrown: ${String(value)}`;
  }
}
